/**
 * Shell for Mastra chat.
 *
 * Runs on native, desktop web, and mobile web. Has a header with a
 * menu button (left) and a close button (right).
 *
 * Note: mobile web keyboard locking (iOS Safari layout shift prevention) is
 * not yet implemented here — see useMobileWebChatLock if that's needed.
 */

import type { ReactNode } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
    CloseButton,
    IconButton,
    isNonMobileWeb,
    MenuIcon,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { BackgroundGradient } from '../../../common/components/BackgroundGradient';
import { MobileWebChatContainerBottomContext } from './MobileWebChatLayoutContext';

interface MastraChatShellProps {
    children: ReactNode;
    onClose?: () => void;
    onMenuPress?: () => void;
    headerAccessory?: ReactNode;
}

export const MastraChatShell = ({
    children,
    onClose,
    onMenuPress,
    headerAccessory,
}: MastraChatShellProps) => {
    const { top } = useSafeAreaInsets();

    return (
        // No mobile-web locking, so mobileWebContainerBottom is always null.
        <MobileWebChatContainerBottomContext.Provider value={null}>
            <View flex={1}>
                <BackgroundGradient />
                <XStack
                    width="100%"
                    paddingTop={top + (isNonMobileWeb ? 24 : 0)}
                    paddingBottom="$3"
                    paddingHorizontal="$6"
                    justifyContent="space-between"
                    alignItems="center"
                    zIndex={1}
                >
                    {onMenuPress != null ? (
                        <IconButton
                            variant="secondary"
                            icon={MenuIcon}
                            onPress={onMenuPress}
                        />
                    ) : (
                        <View width={40} />
                    )}
                    <XStack gap="$2" alignItems="center">
                        {headerAccessory}
                        <CloseButton
                            background="gray"
                            padding={8}
                            onPress={onClose}
                        />
                    </XStack>
                </XStack>
                <YStack flex={1} minHeight={0}>
                    {children}
                </YStack>
            </View>
        </MobileWebChatContainerBottomContext.Provider>
    );
};
